import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type {
  ConnectPaymentProviderBody,
  PaymentProviderConfig,
  PaymentSuccess,
} from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const providerKeys = {
  list: (restaurantId: string) => ['payment-providers', restaurantId] as const,
};

export function usePaymentProviders() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: providerKeys.list(restaurantId),
    queryFn: async () => {
      return unwrapResult<PaymentProviderConfig[]>(
        await openapiClient.GET('/api/restaurants/{restaurantId}/payment-providers', {
          params: { path: { restaurantId } },
        }),
      );
    },
    enabled: !!restaurantId,
  });
}

export function useConnectPaymentProvider() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    meta: { handlesOwnError: true },
    mutationFn: async (body: ConnectPaymentProviderBody) =>
      unwrapResult<PaymentProviderConfig>(
        await openapiClient.POST('/api/restaurants/{restaurantId}/payment-providers', {
          params: { path: { restaurantId } },
          body,
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.list(restaurantId) });
      // The order-setup "Card payments" section + storefront checkout only show
      // online once an ACTIVE provider exists - refresh that derived config too.
      queryClient.invalidateQueries({ queryKey: ['order-setup', restaurantId] });
    },
  });
}

export function useSetProviderActivation() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      provider,
      mode,
      isActive,
    }: {
      provider: PaymentProviderConfig['provider'];
      mode: PaymentProviderConfig['mode'];
      isActive: boolean;
    }) =>
      unwrapResult<PaymentProviderConfig>(
        await openapiClient.PATCH('/api/restaurants/{restaurantId}/payment-providers/{provider}', {
          params: { path: { restaurantId, provider } },
          body: { mode, isActive },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.list(restaurantId) });
      // Activating/deactivating flips whether online shows at checkout + in the
      // order-setup "Card payments" section - refresh that derived config too.
      queryClient.invalidateQueries({ queryKey: ['order-setup', restaurantId] });
    },
  });
}

export function useDisconnectPaymentProvider() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      provider,
      mode,
    }: {
      provider: PaymentProviderConfig['provider'];
      mode: PaymentProviderConfig['mode'];
    }) =>
      unwrapResult<PaymentSuccess>(
        await openapiClient.DELETE('/api/restaurants/{restaurantId}/payment-providers/{provider}', {
          params: { path: { restaurantId, provider }, query: { mode } },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.list(restaurantId) });
      queryClient.invalidateQueries({ queryKey: ['order-setup', restaurantId] });
    },
  });
}
